import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

const benefits = [
  {
    icon: 'ri-shield-check-line',
    title: 'Code Compliant',
    description: 'All framing work meets or exceeds local building codes and regulations'
  },
  {
    icon: 'ri-time-line',
    title: 'On-Time Delivery',
    description: 'Efficient project management ensures your framing is completed on schedule'
  },
  {
    icon: 'ri-tools-line',
    title: 'Expert Craftsmanship',
    description: 'Skilled carpenters with decades of combined framing experience'
  },
  {
    icon: 'ri-price-tag-3-line',
    title: 'Competitive Pricing',
    description: 'Quality framing services at fair, transparent pricing with no hidden costs'
  }
];

const processSteps = [
  {
    number: '01',
    title: 'Site Assessment',
    description: 'We evaluate your project site, review plans, and identify any potential challenges'
  },
  {
    number: '02',
    title: 'Material Selection',
    description: 'Choose from quality lumber options and engineered materials suited for your project'
  },
  {
    number: '03',
    title: 'Foundation Prep',
    description: 'Ensure proper foundation and layout before beginning the framing process'
  },
  {
    number: '04',
    title: 'Frame Construction',
    description: 'Expert installation of walls, floors, roof trusses, and structural components'
  },
  {
    number: '05',
    title: 'Inspection & Quality Check',
    description: 'Thorough inspection to ensure structural integrity and code compliance'
  }
];

const projectGallery = [
  {
    id: 1,
    title: 'Residential Frame',
    location: 'Tolland, CT',
    type: 'Before',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/55370034d56c2f2e3dad6b11ffbd1a92.jpeg'
  },
  {
    id: 2,
    title: 'Residential Frame',
    location: 'Tolland, CT',
    type: 'After',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/ff53e145b8da8a191c710e7b06269004.png'
  },
  {
    id: 3,
    title: 'Custom Home Addition Frame',
    location: 'Denver, CO',
    type: 'Before',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/00a7147e97d782c5846b1cda009fd0e2.png'
  },
  {
    id: 4,
    title: 'Custom Home Addition Frame',
    location: 'Denver, CO',
    type: 'After',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/2d36a36129f282f328e333409870e20e.jpeg'
  },
  {
    id: 5,
    title: 'Complete Framing Building',
    location: 'Watertown, CT',
    type: 'Progress',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/6c274bd22ed50725ad9b3fca22135ce0.jpeg'
  },
  {
    id: 6,
    title: 'Complete Framing Building',
    location: 'Watertown, CT',
    type: 'After',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/282396a2404297c1088728f216750eb3.png'
  },
  {
    id: 7,
    title: 'Complete House Framing',
    location: 'Norwalk, CT',
    type: 'Front',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/8890a16cd64383957ea0805a0e4c135a.jpeg'
  },
  {
    id: 8,
    title: 'Complete House Framing',
    location: 'Norwalk, CT',
    type: 'Back',
    image: 'https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/e5f8de66b070d3dd8605e6dfedc94c08.jpeg'
  }
];

export default function FramingService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/');
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/f974ae4a89bf06aece5244b03610bc1c/ff53e145b8da8a191c710e7b06269004.png"
            alt="Framing background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-brand/20 text-brand text-sm font-semibold rounded-full border border-brand/30">
                Professional Framing Services
              </span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Expert Structural Framing Solutions
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              From residential homes to commercial buildings, we deliver precision framing that forms the backbone of your construction project. Our experienced team ensures structural integrity, code compliance, and timely completion.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToContact}
                className="bg-brand text-white px-8 py-4 rounded-xl font-semibold hover:bg-brand-dark transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                Get Free Estimate
                <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <a
                href="#gallery"
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20 whitespace-nowrap cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                View Projects
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Service Description */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Building Strong Foundations for Your Vision
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                Framing is the skeleton of any construction project, and getting it right is crucial. Our team specializes in both traditional stick framing and modern engineered lumber systems, ensuring your structure is built to last.
              </p>
              <p className="text-gray-600 text-lg mb-8 leading-relaxed">
                We handle residential framing for new homes, additions, and remodels, as well as commercial framing for office buildings, retail spaces, and multi-family housing. Every project receives the same attention to detail and commitment to quality.
              </p>
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-2xl text-brand"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">Residential &amp; Commercial</h4>
                    <p className="text-gray-600">Experience with projects of all sizes and complexities</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-2xl text-brand"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">Quality Materials</h4>
                    <p className="text-gray-600">Premium lumber and engineered products for superior strength</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1">
                    <i className="ri-checkbox-circle-fill text-2xl text-brand"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900 mb-1">Licensed &amp; Insured</h4>
                    <p className="text-gray-600">Fully licensed contractors with comprehensive insurance coverage</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/ac0817f96a0b639e78624b9142094602.jpg"
                  alt="Professional Framing Work"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-brand text-white p-8 rounded-2xl shadow-xl max-w-xs">
                <div className="text-5xl font-bold mb-2">12+</div>
                <div className="text-amber-100">Years of Framing Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Framing Services
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              We combine traditional craftsmanship with modern techniques to deliver exceptional results
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="w-14 h-14 flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-5xl text-brand`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Framing Process
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              A systematic approach that ensures quality and efficiency at every stage
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-brand to-brand-dark text-white rounded-2xl p-8 h-full shadow-lg hover:shadow-xl transition-all">
                  <div className="text-6xl font-bold text-white mb-4">
                    {step.number}
                  </div>
                  <h3 className="text-xl font-bold mb-3">
                    {step.title}
                  </h3>
                  <p className="text-amber-50 leading-relaxed">
                    {step.description}
                  </p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-8 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-3xl text-brand"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section id="gallery" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Completed Framing Projects
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Browse our portfolio of successfully completed framing projects showcasing our expertise and attention to detail
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {projectGallery.map((project) => (
              <div
                key={project.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
              >
                <div className="relative w-full h-80 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 right-4">
                    <span className="inline-block px-4 py-2 bg-brand text-white text-sm font-semibold rounded-full shadow-lg">
                      {project.type}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {project.title}
                  </h3>
                  <div className="flex items-center text-gray-500">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_be1660dc7ada13b6454bff4475ec0778_73e3b339.jpg"
            alt="Framing background"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to Start Your Framing Project?
          </h2>
          <p className="text-xl text-amber-50 mb-10 leading-relaxed">
            Get a free, no-obligation estimate for your residential or commercial framing needs. Our team is ready to bring your vision to life with expert craftsmanship and reliable service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={scrollToContact}
              className="bg-brand text-white px-10 py-4 rounded-xl font-semibold hover:bg-brand-dark transition-all hover:scale-105 whitespace-nowrap cursor-pointer shadow-xl"
            >
              Request Free Estimate
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="bg-white/10 backdrop-blur-sm text-white px-10 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20 whitespace-nowrap cursor-pointer"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
